import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Search, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { API, http, authHeaders, formatApiError, CAREERS_TOKEN } from "../../../lib/api";

const STATUS_STYLE = { done: "bg-emerald-500/15 text-emerald-300", failed: "bg-red-500/15 text-red-300", not_connected: "bg-white/5 text-zinc-400", pending: "bg-white/5 text-zinc-300" };
const STATUS_LABEL = { done: "Done", failed: "Failed", not_connected: "Not connected", pending: "Pending" };
const SOURCE = { web: "Web form", web_chat: "Web chat", whatsapp: "WhatsApp" };

const Chip = ({ label, s }) => (
  <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] ${STATUS_STYLE[s] ?? STATUS_STYLE.pending}`}>{label}: {STATUS_LABEL[s] ?? s}</span>
);

export const ApplicationsTab = ({ onAuthError }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`${API}/careers/admin/applications`, { headers: authHeaders(CAREERS_TOKEN) });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onAuthError();
      else toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return items.filter((a) => (kind === "all" || a.kind === kind) && [a.name, a.email, a.phone, a.job_code, a.vertical, a.role, a.reference].join(" ").toLowerCase().includes(t));
  }, [items, kind, q]);

  const download = async (a) => {
    try {
      const res = await http.get(`${API}/careers/admin/applications/${a.id}/cv`, { headers: authHeaders(CAREERS_TOKEN), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${a.reference}-${a.name.replace(/\s+/g, "_")}.${a.cv_filename.split(".").pop()}`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Couldn't download this CV.");
    }
  };

  const retry = async (a) => {
    setBusyId(a.id);
    try {
      const { data } = await http.post(`${API}/careers/admin/applications/${a.id}/retry`, {}, { headers: authHeaders(CAREERS_TOKEN) });
      setItems((p) => p.map((x) => (x.id === a.id ? data : x)));
      toast.success("Retried pending steps");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="applications-tab">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input className="field" style={{ paddingLeft: 28 }} placeholder="Search name, email, Job ID, vertical, role, reference" value={q} onChange={(e) => setQ(e.target.value)} data-testid="applications-search" />
        </div>
        <select className="field md:w-56" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="applications-kind-filter">
          <option value="all">All submissions</option>
          <option value="job">Job applications</option>
          <option value="general">General talent record</option>
        </select>
        <button onClick={load} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm hover:border-emerald-400" data-testid="applications-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      <div className="mt-6 overflow-x-auto rounded-md border border-white/10">
        <table className="w-full min-w-[1100px] text-sm" data-testid="applications-table">
          <thead className="bg-[#111519] text-left text-xs font-mono uppercase tracking-[0.12em] text-zinc-400">
            <tr>{["Received", "Candidate", "Applied for", "Vertical / Role", "Source", "Pipeline", ""].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-400" /></td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-400" data-testid="applications-empty">No submissions yet.</td></tr>}
            {!loading && filtered.map((a) => (
              <tr key={a.id} className="border-t border-white/10 align-top" data-testid={`application-row-${a.reference}`}>
                <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{new Date(a.created_at).toLocaleDateString("en-IN")}<p className="font-mono text-[11px] text-zinc-500">{a.reference}</p></td>
                <td className="px-4 py-3"><p>{a.name}</p><p className="text-xs text-zinc-400">{a.email}</p><p className="text-xs text-zinc-400">{a.phone}</p></td>
                <td className="px-4 py-3">{a.kind === "job" ? <><p className="font-mono text-emerald-300">{a.job_code}</p><p className="text-xs text-zinc-400">{a.job_title}</p></> : <span className="text-zinc-300">General CV</span>}</td>
                <td className="px-4 py-3"><p>{a.vertical}</p><p className="text-xs text-zinc-400">{a.role}</p></td>
                <td className="px-4 py-3 text-zinc-300">{SOURCE[a.source] ?? a.source}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <span className="flex items-center gap-1"><Chip label="Drive" s={a.drive_status} />{a.drive_link && <a href={a.drive_link} target="_blank" rel="noreferrer" className="text-emerald-300" aria-label="Open in Drive"><ExternalLink className="h-3 w-3" /></a>}</span>
                    <Chip label="CV email" s={a.owner_email_status} />
                    <Chip label="Confirmation" s={a.candidate_email_status} />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => download(a)} className="rounded p-2 text-zinc-300 hover:text-emerald-300" aria-label="Download CV" data-testid={`application-download-${a.reference}`}><Download className="h-4 w-4" /></button>
                    <button onClick={() => retry(a)} disabled={busyId === a.id} className="rounded p-2 text-zinc-300 hover:text-emerald-300 disabled:opacity-50" aria-label="Retry pending steps" data-testid={`application-retry-${a.reference}`}>
                      {busyId === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
